import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Gem, Sparkles, RefreshCw, Save, User } from 'lucide-react';
import api from '../services/api';
import Navbar from '../components/Navbar';
import LoadingTokensModal from '../components/LoadingTokensModal';
import VipModal from '../components/VipModal';
import Button from '../components/ui/Button';
import { trackViewContent } from '../utils/metaPixel';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showVipModal, setShowVipModal] = useState(false);
  const [showLoadingTokensModal, setShowLoadingTokensModal] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    endereco: '',
  });

  useEffect(() => {
    trackViewContent('profile_page', 'page', { content_name: 'Profile' });
  }, []);

  useEffect(() => {
    if (!user) return;
    setFormData({
      nome: user.nome || '',
      email: user.email || '',
      telefone: user.telefone || '',
      endereco: user.endereco || '',
    });
  }, [user]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.put('/users/profile', formData);
      updateUser(response.data.user);
      setSuccess('Perfil atualizado com sucesso!');
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao atualizar perfil');
    } finally {
      setLoading(false);
    }
  };

  const refreshUserData = async () => {
    try {
      const response = await api.get('/users/me');
      updateUser(response.data.user);
    } catch (err) {
      console.error('Erro ao atualizar dados do usuário:', err);
    }
  };

  const inputClass =
    'w-full rounded-lg border border-zinc-700 bg-zinc-800 px-4 py-2.5 text-white placeholder-zinc-500 transition-colors focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500';

  return (
    <div className="min-h-screen bg-zinc-950">
      <Navbar />

      <div className="mx-auto max-w-md px-4 pb-10 pt-20">
        <h1 className="mb-6 text-center text-xl font-semibold text-white">Meu perfil</h1>

        <div className="mb-6 rounded-2xl border border-white/10 bg-zinc-900 p-6">
          <div className="mb-5 text-center">
            <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-rose-600 text-xl font-semibold text-white">
              {user?.nome?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <h2 className="text-lg font-semibold text-white">{user?.nome || 'Usuário'}</h2>
            <p className="text-sm text-zinc-400">{user?.email}</p>
          </div>

          <div className="mb-5 flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 py-3">
            <Gem className="h-4 w-4 text-rose-400" />
            <span className="text-lg font-semibold text-white">{user?.tokens || 0}</span>
            <span className="text-sm text-zinc-400">tokens</span>
          </div>

          <div className="space-y-2.5">
            <Button
              onClick={() => setShowLoadingTokensModal(true)}
              fullWidth
              icon={Gem}
            >
              Carregar tokens
            </Button>
            <Button
              onClick={() => navigate('/upload')}
              fullWidth
              variant="secondary"
              icon={Sparkles}
            >
              Experimentar roupas
            </Button>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-zinc-900 p-6">
          <h3 className="mb-5 flex items-center justify-center gap-2 text-base font-semibold text-white">
            <User className="h-4 w-4 text-rose-400" /> Informações pessoais
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            {[
              { id: 'nome', label: 'Nome completo', type: 'text', placeholder: 'Seu nome completo' },
              { id: 'email', label: 'Email', type: 'email', placeholder: 'seu@email.com' },
              { id: 'telefone', label: 'Telefone', type: 'tel', placeholder: '(11) 99999-9999' },
              { id: 'endereco', label: 'Endereço', type: 'text', placeholder: 'Sua cidade, estado' },
            ].map(({ id, label, type, placeholder }) => (
              <div key={id}>
                <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-zinc-300">
                  {label}
                </label>
                <input
                  type={type}
                  id={id}
                  name={id}
                  value={formData[id]}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder={placeholder}
                />
              </div>
            ))}

            {error && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">
                {error}
              </div>
            )}
            {success && (
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-sm text-emerald-300">
                {success}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                onClick={refreshUserData}
                variant="secondary"
                fullWidth
                icon={RefreshCw}
              >
                Atualizar
              </Button>
              <Button type="submit" fullWidth loading={loading} icon={Save}>
                {loading ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </form>
        </div>
      </div>

      <VipModal isOpen={showVipModal} onClose={() => setShowVipModal(false)} />
      <LoadingTokensModal
        isOpen={showLoadingTokensModal}
        onClose={() => setShowLoadingTokensModal(false)}
      />
    </div>
  );
};

export default Profile;
